import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TrainingLessonRequestOptions } from '@models/types'

const watchEntityKeys = ['trainingLessons']

export type TrainingLessonsFilters = {
  string?: string,
  training_module_id?: number,
}

interface UseTrainingLessonsOptions {
  entityKey?: string,
  filters?: TrainingLessonsFilters,
  performHttpRequests?: boolean,
  requestOptions?: TrainingLessonRequestOptions,
}

function useTrainingLessons(options: UseTrainingLessonsOptions = {}) {
  const { filters = {}, requestOptions } = options
  const { string: filterString, training_module_id: filterTrainingModuleId } = filters

  const {
    updatedEntities: { trainingLessons: trainingLessonsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const trainingLessons = useSelector(reduxState => reduxState.entities.trainingLessons)

  const filteredTrainingLessons = useMemo(() => {
    const filtered = Object.values(trainingLessons).filter((trainingLesson) => {
      const { id, title, training_module_id } = trainingLesson

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchTrainingModule = training_module_id === filterTrainingModuleId

      return matchTrainingModule && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [trainingLessonsUpdatedAt, JSON.stringify(filters)])

  const filteredTrainingLessonCount = filteredTrainingLessons.length
  const hasFilteredTrainingLesson = !!filteredTrainingLessonCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTrainingLessonCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTrainingLesson } = useReduxAction(
    'trainingLessons',
    'loadTrainingLessons',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrainingLessonCount,
    filteredTrainingLessons,
    hasFilteredTrainingLesson,
    loading: loadingTrainingLesson,
  }
}

export default useTrainingLessons
